import type { FilterObject } from 'app/filter.utils'
import { filtersToSearch } from 'lib/listSearch'
import { tagsPages } from 'lib/recordTypes'

export type ListTemplateRecordType = `contacts` | `activities`

export type ListTemplate = {
  id: string
  recordType: ListTemplateRecordType
  name: string
  blurb: string
  filters: FilterObject[]
}

const ACTIVITY_BLURBS: Record<keyof typeof tagsPages, string> = {
  'missed-call': `Calls nobody picked up, so somebody can call back.`,
  callback: `Callers who asked to be called back.`,
  voicemail: `Calls that went to voicemail.`,
  'not-scheduled': `Conversations that ended without an appointment.`,
  canceled: `Appointments that were canceled.`,
  scheduled: `Conversations that booked an appointment.`,
  rescheduled: `Appointments that moved to another time.`,
  confirmation: `Calls confirming an upcoming appointment.`,
}

/**
 * Production's predefined lists, as templates. The contacts rules are the ones
 * `useContactsPage` and `useSidebarCounts` apply to the same tabs, except that
 * "from today" is the relative `today_or_later` window (today included, like the tab's
 * `>= today`): a saved filter that read today's date at creation would freeze on that day.
 */
export const LIST_TEMPLATES: ReadonlyArray<ListTemplate> = [
  {
    id: `contacts:scheduled-leads`,
    recordType: `contacts`,
    name: `Scheduled Leads`,
    blurb: `Leads with an appointment on the books.`,
    filters: [
      { attribute: `leadStatus.keyword`, operator: `in`, value: [`Scheduled`] },
      { attribute: `nearestAppointmentDate`, operator: `between`, value: `today_or_later` },
    ] as FilterObject[],
  },
  {
    id: `contacts:closed-lost-leads`,
    recordType: `contacts`,
    name: `Closed Lost Leads`,
    blurb: `Leads marked Closed Lost, to review or win back.`,
    filters: [{ attribute: `leadStatus.keyword`, operator: `in`, value: [`Closed Lost`] }] as FilterObject[],
  },
  ...Object.entries(tagsPages).map(
    ([key, label]): ListTemplate => ({
      id: `activities:${key}`,
      recordType: `activities`,
      name: label,
      blurb: ACTIVITY_BLURBS[key],
      filters: [{ attribute: `tags`, operator: `in`, value: [label] }] as FilterObject[],
    }),
  ),
]

/** `activitiesBase` is the caller's exclusion term for activities templates; contacts templates ignore it. */
export function templateSearch(template: ListTemplate, activitiesBase: string): string {
  const terms = filtersToSearch(template.filters)
  return template.recordType === `activities` ? `${activitiesBase} ${terms}` : terms
}
